import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { colors, glazingTypes, profileSystems } from '../data/mockData';
import ColorPicker from './ColorPicker';
import ConfiguratorAdditionalItems from './ConfiguratorAdditionalItems';
import ProfileSystemPicker from './ProfileSystemPicker';
import { useTranslation } from '../context/TranslationContext';
import {
  getSchuifpuiMinWidthMm,
  SCHUIFPUI_MIN_HEIGHT_MM,
  SCHUIFPUI_MAX_HEIGHT_MM,
  SCHUIFPUI_MAX_WIDTH_MM,
} from '../lib/pdfPricing';

const SchuifpuiOptionsPanel = ({
  config,
  onConfigChange,
  additionalItems,
  onAdditionalItemsChange,
}) => {
  const { t } = useTranslation();
  
  const translateGlazingTypeName = (id) => {
    const translations = {
      'double': t('options.doubleGlass24'),
      'triple': t('options.doubleGlass36'),
      'double-24': t('options.doubleGlass24'),
      'double-36': t('options.doubleGlass36')
    };
    return translations[id] || id;
  };
  
  const translateGlazingTypeDescription = (id) => {
    const translations = {
      'double': t('options.doubleGlass24Desc'),
      'triple': t('options.doubleGlass36Desc'),
      'double-24': t('options.doubleGlass24Desc'),
      'double-36': t('options.doubleGlass36Desc')
    };
    return translations[id] || '';
  };

  const slidingPositions = [
    { id: 'left', name: t('options.slidingLeft'), description: t('options.slidingLeftDesc'), image: process.env.PUBLIC_URL + '/images/slide-left.png' },
    { id: 'right', name: t('options.slidingRight'), description: t('options.slidingRightDesc'), image: process.env.PUBLIC_URL + '/images/slide-right.png' },
    { id: 'center', name: t('options.slidingCenter'), description: t('options.slidingCenterDesc'), image: process.env.PUBLIC_URL + '/images/slide-center.png' }
  ];

  const thresholdHeights = [
    { id: '30mm', name: '30 mm', value: 30 },
    { id: '55mm', name: '55 mm', value: 55 }
  ];

  const [widthInput, setWidthInput] = useState(String(config.width));
  const [heightInput, setHeightInput] = useState(String(config.height));

  useEffect(() => {
    setWidthInput(String(config.width));
  }, [config.width]);

  useEffect(() => {
    setHeightInput(String(config.height));
  }, [config.height]);

  const minWidthMm = getSchuifpuiMinWidthMm(config.slidingPosition);

  const commitDimension = (field, rawValue, min, max) => {
    const parsed = parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) {
      const fallback = String(config[field]);
      if (field === 'width') setWidthInput(fallback);
      if (field === 'height') setHeightInput(fallback);
      return;
    }
    const clamped = Math.max(min, Math.min(max, parsed));
    onConfigChange(field, clamped);
  };

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden p-4 md:p-5 xl:p-6 bg-white border-r border-gray-200 touch-manipulation">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('options.slidingDoorConfiguration')}</h2>
          <p className="text-sm text-gray-500 mt-1">{t('options.configureSlidingDoor')}</p>
        </div>

        <ProfileSystemPicker
          profileSystems={profileSystems}
          config={config}
          onConfigChange={onConfigChange}
          translateName={(id, name) => ({ schuco: t('options.schuco'), synego: t('options.synego') }[id] || name)}
          translateDescription={(id, description) => ({ schuco: t('options.schucoDesc'), synego: t('options.synegoDesc') }[id] || description)}
        />

        {/* Dimensions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.dimensions')}</CardTitle>
            <CardDescription className="text-xs">{t('options.setDimensions')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="door-width" className="text-sm font-medium">{t('options.width')}</Label>
                <Input
                  id="door-width"
                  type="number"
                  value={widthInput}
                  onChange={(e) => setWidthInput(e.target.value)}
                  onBlur={() => commitDimension('width', widthInput, minWidthMm, SCHUIFPUI_MAX_WIDTH_MM)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitDimension('width', widthInput, minWidthMm, SCHUIFPUI_MAX_WIDTH_MM); }}
                  min={minWidthMm}
                  max={SCHUIFPUI_MAX_WIDTH_MM}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="door-height" className="text-sm font-medium">{t('options.height')}</Label>
                <Input
                  id="door-height"
                  type="number"
                  value={heightInput}
                  onChange={(e) => setHeightInput(e.target.value)}
                  onBlur={() => commitDimension('height', heightInput, SCHUIFPUI_MIN_HEIGHT_MM, SCHUIFPUI_MAX_HEIGHT_MM)}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitDimension('height', heightInput, SCHUIFPUI_MIN_HEIGHT_MM, SCHUIFPUI_MAX_HEIGHT_MM); }}
                  min={SCHUIFPUI_MIN_HEIGHT_MM}
                  max={SCHUIFPUI_MAX_HEIGHT_MM}
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sliding Position */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.slidingPosition')}</CardTitle>
            <CardDescription className="text-xs">Do you want the sliding section viewed from inside in the left part, right part, or center?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3">
              {slidingPositions.map((position) => (
                <button
                  key={position.id}
                  onClick={() => onConfigChange('slidingPosition', position.id)}
                  className={`relative rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 overflow-hidden ${
                    config.slidingPosition === position.id
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`w-full aspect-video flex items-center justify-center p-2 ${
                    config.slidingPosition === position.id ? 'bg-white' : 'bg-gray-50'
                  }`}>
                    <img
                      src={position.image}
                      alt={position.name}
                      className="max-h-full max-w-full object-contain w-full h-full"
                      style={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className={`text-xs font-semibold text-center py-2 px-1 ${
                    config.slidingPosition === position.id ? 'text-orange-600' : 'text-gray-900'
                  }`}>{position.name}</div>
                  {position.description && (
                    <div className="text-[10px] text-gray-500 text-center px-1 pb-2">{position.description}</div>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Threshold */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.threshold')}</CardTitle>
            <CardDescription className="text-xs">Do you want to set the threshold height at 30 mm or 55 mm for the door?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {thresholdHeights.map((height) => (
                <button
                  key={height.id}
                  onClick={() => onConfigChange('thresholdHeight', height.id)}
                  className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                    config.thresholdHeight === height.id
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`text-sm font-medium ${
                    config.thresholdHeight === height.id ? 'text-orange-600' : 'text-gray-900'
                  }`}>{height.name}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Glazing Type - Right Part */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.glazingTypeRightPart')}</CardTitle>
            <CardDescription className="text-xs">Select the desired glazing type for the right part</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {glazingTypes.map(glazing => (
                <button
                  key={glazing.id}
                  onClick={() => onConfigChange('rightGlazingType', glazing.id)}
                  className={`w-full p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-left flex items-center space-x-3 ${
                    config.rightGlazingType === glazing.id
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {glazing.image && (
                    <div className="w-16 h-16 flex-shrink-0 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                      <img
                        src={glazing.image}
                        alt={glazing.name}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  <div>
                    <div className={`text-sm font-semibold ${config.rightGlazingType === glazing.id ? 'text-orange-600' : 'text-gray-900'}`}>
                      {translateGlazingTypeName(glazing.id)}
                    </div>
                    {glazing.description && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {translateGlazingTypeDescription(glazing.id)}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Exterior Frame Color */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.exteriorColor')}</CardTitle>
            <CardDescription className="text-xs">Select from standard colors or search RAL codes</CardDescription>
          </CardHeader>
          <CardContent>
            <ColorPicker
              selectedColor={config.exteriorColor}
              onColorChange={(colorId) => onConfigChange('exteriorColor', colorId)}
              showSearch={true}
              maxVisible={8}
            />
          </CardContent>
        </Card>

        {/* Interior Frame Color */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('options.interiorColor')}</CardTitle>
            <CardDescription className="text-xs">Select from standard colors or search RAL codes</CardDescription>
          </CardHeader>
          <CardContent>
            <ColorPicker
              selectedColor={config.interiorColor}
              onColorChange={(colorId) => onConfigChange('interiorColor', colorId)}
              showSearch={true}
              maxVisible={8}
            />
          </CardContent>
        </Card>
        {onAdditionalItemsChange ? (
          <ConfiguratorAdditionalItems
            items={additionalItems}
            onItemsChange={onAdditionalItemsChange}
          />
        ) : null}
      </div>
    </div>
  );
};

export default SchuifpuiOptionsPanel;

